import type { PreferredSchedule } from "@/config/contact";
import api from "./api";

export type BusinessType = "consultorio" | "evento" | "agencia" | "empresa";

export type QuoteStatus = "nuevo" | "contactado" | "cotizado" | "cerrado";

/** Un lead puede ser una cotización corporativa o un pedido de llamada. */
export type QuoteKind = "cotizacion" | "llamada";

/** Un lead llega por el formulario o como referido en la postventa. */
export type QuoteOrigin = "form" | "referido";

export interface Quote {
  _id: string;
  kind?: QuoteKind;
  name: string;
  /** Ausente en un pedido de llamada. */
  email?: string;
  phone: string;
  /** Ausente en un pedido de llamada. */
  businessType?: BusinessType;
  /** Ausente en un pedido de llamada. */
  quantity?: number;
  preferredSchedule?: PreferredSchedule;
  company?: string;
  product?: string;
  targetDate?: string;
  logo?: string;
  message?: string;
  status: QuoteStatus;
  notificationStatus?: "sent" | "failed";
  notificationError?: string;
  /** Instante del último cambio de estado: base de la antigüedad del lead.
   *  Falta en los leads creados antes de que existiera el seguimiento. */
  statusChangedAt?: string;
  /** Seguimiento del lead; el token de baja nunca se expone en el panel. */
  nurture?: {
    stepsSent: number;
    optOutAt?: string | null;
  };
  /** Origen del lead: del formulario o traído por otro cliente. */
  origin?: QuoteOrigin;
  /** Cotización que refirió a este lead (cuando `origin` es `referido`). */
  referredByQuoteId?: string;
  /** Nombre del cliente que refirió (snapshot para el panel). */
  referredByName?: string;
  /** Estado de la postventa; el token del enlace nunca se expone en el panel. */
  postventa?: {
    recognitionSentAt?: string | null;
    recognitionStatus?: "sent" | "failed" | null;
  };
  createdAt: string;
  updatedAt: string;
}

/** Datos que envía el formulario público de cotización. */
export interface QuoteRequestInput {
  name: string;
  email: string;
  phone: string;
  businessType: BusinessType;
  quantity: number;
  company?: string;
  product?: string;
  targetDate?: string;
  message?: string;
  /** Logo como dataURL; el backend lo sube a R2 y guarda la URL. */
  image?: string;
  /** Honeypot anti-bot: siempre vacío. */
  website?: string;
}

/** Datos mínimos del mini-form de "solicitar llamada". */
export interface CallbackRequestInput {
  name: string;
  phone: string;
  preferredSchedule: PreferredSchedule;
  /** Honeypot anti-bot: siempre vacío. */
  website?: string;
}

export interface QuoteFilters {
  status?: QuoteStatus | "all";
  page?: number;
  limit?: number;
}

export interface QuotesResponse {
  quotes: Quote[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/** Resumen de postventa de una cotización cerrada (RF-11). */
export interface QuotePostSaleSummary {
  testimonialSubmitted: boolean;
  referralCount: number;
}

/** Estados del ciclo, en orden, con su etiqueta para la UI (fuente única). */
export const QUOTE_STATUSES: ReadonlyArray<{
  value: QuoteStatus;
  label: string;
}> = [
  { value: "nuevo", label: "Nuevo" },
  { value: "contactado", label: "Contactado" },
  { value: "cotizado", label: "Cotizado" },
  { value: "cerrado", label: "Cerrado" },
];

const DAY_MS = 86_400_000;

/** Solo un lead sin cerrar sigue siendo gestionable; uno cerrado no se persigue. */
const OPEN_STATUSES: readonly QuoteStatus[] = ["nuevo", "contactado"];

/**
 * Días sin avance a partir de los cuales el lead se considera frío: por debajo
 * de ese umbral todavía es una gestión en curso y marcarlo sería ruido.
 */
const COLD_LEAD_DAYS = 5;

/** Antigüedad desde el último avance del lead y si el panel debe destacarlo. */
export function getLeadFreshness(
  quote: Pick<Quote, "status" | "statusChangedAt" | "nurture" | "createdAt">,
  now: Date = new Date()
): { days: number; isCold: boolean } {
  // Un lead anterior al seguimiento no tiene `statusChangedAt`: se usa su
  // creación para no perder la señal de "frío" ni romper el panel.
  const stamp = quote.statusChangedAt ?? quote.createdAt;
  const days = stamp
    ? Math.floor((now.getTime() - new Date(stamp).getTime()) / DAY_MS)
    : NaN;

  return {
    days,
    isCold:
      Number.isFinite(days) &&
      OPEN_STATUSES.includes(quote.status) &&
      days >= COLD_LEAD_DAYS &&
      !quote.nurture?.optOutAt,
  };
}

class QuoteService {
  /** Registra una solicitud de cotización desde la home (RF-1). */
  async createQuote(
    input: QuoteRequestInput
  ): Promise<{ id: string; status: QuoteStatus }> {
    const response = await api.post("/quotes", input);
    return response.data.quote;
  }

  /** Registra un pedido de llamada desde el canal de contacto. */
  async createCallback(
    input: CallbackRequestInput
  ): Promise<{ id: string; status: QuoteStatus }> {
    const response = await api.post("/quotes", {
      ...input,
      kind: "llamada",
    });
    return response.data.quote;
  }

  /** Listado para el panel del vendedor (RF-6). */
  async getQuotes(filters: QuoteFilters = {}): Promise<QuotesResponse> {
    const params = new URLSearchParams();

    if (filters.status && filters.status !== "all") {
      params.append("status", filters.status);
    }
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());

    const query = params.toString();
    const response = await api.get(`/admin/quotes${query ? `?${query}` : ""}`);

    return {
      quotes: response.data.quotes,
      pagination: response.data.pagination,
    };
  }

  async getQuote(id: string): Promise<Quote> {
    const response = await api.get(`/admin/quotes/${id}`);
    return response.data.quote;
  }

  /** Resumen de postventa de una cotización (RF-11). */
  async getQuotePostSale(id: string): Promise<QuotePostSaleSummary> {
    const response = await api.get(`/admin/quotes/${id}/postventa`);
    return response.data.postventa;
  }

  /** Avanza el ciclo del lead (RF-7). */
  async updateQuoteStatus(id: string, status: QuoteStatus): Promise<Quote> {
    const response = await api.put(`/admin/quotes/${id}/status`, { status });
    return response.data.quote;
  }

  getStatusLabel(status: QuoteStatus): string {
    return (
      QUOTE_STATUSES.find((option) => option.value === status)?.label ?? status
    );
  }

  getStatusColor(status: QuoteStatus): string {
    const colors: Record<QuoteStatus, string> = {
      nuevo: "bg-yellow-100 text-yellow-800",
      contactado: "bg-blue-100 text-blue-800",
      cotizado: "bg-purple-100 text-purple-800",
      cerrado: "bg-green-100 text-green-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  }
}

export default new QuoteService();
